import { AccountField, CompanyField } from 'components/shared/dropdowns'
import { AccountHoldingsDocument, useCreateHoldingMutation } from 'graphql/bindings/AdminBindings'
import {
  Box,
  Button,
  FlagField,
  FormModal,
  TextField,
  ValueAutocompleteField,
  useSnackbar,
} from '@forgeglobal/design-framework'
import { Field, Form, FormRenderProps } from 'react-final-form'
import { createRandomString } from 'util/StringUtils'
import { formatAbbreviatedNumber, parseAbbreviatedNumber } from 'util/NumberUtils'
import React, { useState } from 'react'
import numeral from 'numeral'

interface FormShape {
  account?: { label: string; value: string }
  company?: { label: string; value: number }
  num_shares?: number
  is_estimate?: boolean
  share_class?: string
  date_acquired?: Date
  interest: string[]
  create_and_add_new: boolean
}

const CreateHoldingForm: React.FC<FormRenderProps<FormShape>> = ({ handleSubmit, form, values, valid }) => {
  return (
    <form onSubmit={handleSubmit}>
      <AccountField name="account" label="Account" />
      <CompanyField name="company" label="Issuer" />
      <Field
        name="num_shares"
        parser={parseAbbreviatedNumber}
        formatter={formatAbbreviatedNumber}
        component={TextField}
        label="Number of shares"
        fullWidth
      />
      <Field name="is_estimate" label="Is this an estimated amount?" component={FlagField} fullWidth attached />
      <Field name="share_class" component={TextField} label="Share class (e.g. Series A Common)" fullWidth />
      <ValueAutocompleteField
        name="interest"
        label="Interest"
        multiple
        options={[
          { label: 'Sell', value: 'createAsk' },
          { label: 'Buy More', value: 'createBid' },
        ]}
      />
      <Field name="create_and_add_new" component={FlagField} label="Create and add new" fullWidth />
      <Box mt={2}>
        <Button variant="contained" color="primary" type="submit" disabled={!valid} fullWidth>
          Create
        </Button>
      </Box>
    </form>
  )
}

const validateForm = (values: FormShape) => {
  if (values.account && values.company && values.interest) return undefined
  return [{ error: 'Required Fields Missing' }]
}

interface CreateHoldingModalProps {
  hideModal: () => void
  fromAccount?: {
    accountId: string
    accountName: string
  }
  fromIssuer?: {
    companyId: number
    companyName: string
  }
}

export const CreateHoldingModal: React.FC<CreateHoldingModalProps> = ({ hideModal, fromAccount, fromIssuer }) => {
  const [key, setKey] = useState(createRandomString())
  const [createHolding] = useCreateHoldingMutation({
    onCompleted: () => {
      hideModal()
      setKey(createRandomString())
      enqueueSnackbar('Holding created!', { variant: 'success' })
    },
  })
  const { enqueueSnackbar } = useSnackbar()

  const onSubmit = (values: FormShape) => {
    const numShares = numeral(values?.num_shares).value()
    createHolding({
      variables: {
        form: {
          account_id: values?.account?.value as string,
          company_id: values?.company?.value as number,
          estimated_num_shares: values?.is_estimate ? numShares : undefined,
          confirmed_num_shares: !values?.is_estimate ? numShares : undefined,
          share_class: values?.share_class || undefined,
          create_bid: (values?.interest ?? []).includes('createBid'),
          create_ask: (values?.interest ?? []).includes('createAsk'),
        },
      },
      refetchQueries: [{ query: AccountHoldingsDocument, variables: { accountId: values?.account?.value } }],
    })
  }
  const initialValues: FormShape = {
    account: fromAccount ? { label: fromAccount.accountName, value: fromAccount.accountId } : undefined,
    company: fromIssuer ? { label: fromIssuer.companyName, value: fromIssuer.companyId } : undefined,
    interest: [],
    create_and_add_new: false,
  }
  return (
    <FormModal open hideModal={hideModal} title="Create: Holding">
      <div key={key}>
        <Form<FormShape>
          component={CreateHoldingForm}
          onSubmit={onSubmit}
          validate={validateForm}
          initialValues={{
            ...initialValues,
          }}
        />
      </div>
    </FormModal>
  )
}
